import { Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { ClientPaymentApproverPickerService, ClientPaymentApproverPickerState } from 'src/app/Common/Payment-Approver-Picker/clientPaymentApproverPicker.service';
import { IWeissmanModalComponent } from 'src/app/Compliance/WeissmanModalService';
import { ClientPaymentApproverCandidate } from 'src/app/Core-Repositories/payment.repository';

export type PaymentBatchReviewUpdate = 'no-change' | 'rereviewed';
export type PaymentBatchApproverUpdate = 'no-change' | 'change';

export interface PaymentBatchPaymentsBulkUpdateModalParams {
    paymentIds: number[];
    selectedCount: number;
    canRereview: boolean;
    canChangeApprover: boolean;
}

export interface PaymentBatchPaymentsBulkUpdateModalResult {
    markRereviewed: boolean;
    approverContactId?: number;
}

@Component({
    selector: 'payment-batch-payments-bulk-update-modal',
    templateUrl: './paymentBatchPaymentsBulkUpdateModal.component.html',
    styleUrls: ['./paymentBatchPaymentsBulkUpdateModal.component.scss'],
    standalone: false,
})
export class PaymentBatchPaymentsBulkUpdateModalComponent
    implements OnInit, IWeissmanModalComponent<PaymentBatchPaymentsBulkUpdateModalParams, PaymentBatchPaymentsBulkUpdateModalResult>
{
    constructor(
        private readonly pickerService: ClientPaymentApproverPickerService,
        private readonly bsModalRef: BsModalRef,
    ) {}

    params: PaymentBatchPaymentsBulkUpdateModalParams;
    result: PaymentBatchPaymentsBulkUpdateModalResult;
    reviewUpdate: PaymentBatchReviewUpdate = 'no-change';
    approverUpdate: PaymentBatchApproverUpdate = 'no-change';
    includeAllCompanyContacts = false;
    loading = false;
    candidates: ClientPaymentApproverCandidate[] = [];
    selectedContactId: number = null;
    selectedDisplayName: string = null;

    get headerLabel(): string {
        return `Bulk Update ${this.params.selectedCount} ${this.params.selectedCount === 1 ? 'Payment' : 'Payments'}`;
    }

    get disableSave(): boolean {
        const hasReviewUpdate = this.params.canRereview && this.reviewUpdate === 'rereviewed';
        const hasApproverUpdate = this.params.canChangeApprover && this.approverUpdate === 'change' && !!this.selectedContactId;
        return this.loading || (!hasReviewUpdate && !hasApproverUpdate) || (this.approverUpdate === 'change' && !this.selectedContactId);
    }

    async ngOnInit(): Promise<void> {
        // Review Status and Approver Update should always default to 'No Change', regardless of permission.
        this.reviewUpdate = 'no-change';
        this.approverUpdate = 'no-change';
        if (this.params.canChangeApprover) {
            this.loading = true;
            try {
                this.applyPickerState(await this.pickerService.loadInitial({ paymentIds: this.params.paymentIds }));
            } finally {
                this.loading = false;
            }
        }
    }

    async onIncludeAllCompanyContactsChanged(): Promise<void> {
        this.pickerService.saveShowAllPreference(this.includeAllCompanyContacts);
        await this.loadCandidates();
    }

    onApproverSelected(event: TypeaheadMatch): void {
        const candidate = event.item as ClientPaymentApproverCandidate;
        this.selectedContactId = candidate.contactId;
        this.selectedDisplayName = candidate.displayName;
    }

    onApproverTextChanged(displayName: string): void {
        this.selectedDisplayName = displayName;
        const selectedCandidate = this.candidates.find((x) => x.contactId === this.selectedContactId);
        if (selectedCandidate?.displayName !== displayName) this.selectedContactId = null;
    }

    save(): void {
        if (this.disableSave) return;
        this.result = {
            markRereviewed: this.params.canRereview && this.reviewUpdate === 'rereviewed',
            approverContactId: this.params.canChangeApprover && this.approverUpdate === 'change' ? this.selectedContactId : undefined,
        };
        this.bsModalRef.hide();
    }

    cancel(): void {
        this.bsModalRef.hide();
    }

    private async loadCandidates(): Promise<void> {
        this.loading = true;
        try {
            this.applyPickerState(await this.pickerService.load(
                { paymentIds: this.params.paymentIds }, this.includeAllCompanyContacts, this.selectedContactId,
            ));
        } finally {
            this.loading = false;
        }
    }

    private applyPickerState(state: ClientPaymentApproverPickerState): void {
        this.candidates = state.candidates;
        this.includeAllCompanyContacts = state.includeAllCompanyContacts;
        this.selectedContactId = state.selectedContactId;
        if (!this.selectedContactId) this.selectedDisplayName = null;
    }
}
